<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ─────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3">
              <i class="fe fe-slash" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Zero-Enrollee Facilities</h4>
              <small style="opacity: 0.8;">
                Review facilities with no enrollees, then disapprove the ones you no longer want active
              </small>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── Controls ────────────────────────────────────────── -->
        <div class="card shadow-sm mb-4">
          <div class="card-body d-flex flex-wrap align-items-center" style="gap: 10px;">
            <button class="btn btn-outline-primary btn-sm" @click="loadZeroEnrolleeFacilities" :disabled="loading">
              <i class="fe fe-refresh-cw mr-1"></i> {{ loading ? 'Loading…' : 'Refresh List' }}
            </button>
            <span class="text-muted small" v-if="loaded">
              {{ matched.length }} facilities with zero enrollees
              ({{ alreadyDisapprovedCount }} already disapproved, {{ selectedCount }} selected)
              <template v-if="unmatched.length"> · {{ unmatched.length }} unmatched record(s) need manual review</template>
            </span>
          </div>
        </div>

        <!-- ── Unmatched / broken records ──────────────────────── -->
        <div class="card shadow-sm mb-4" v-if="unmatched.length">
          <div class="card-header font-weight-bold text-warning">
            <i class="fe fe-alert-triangle mr-1"></i> Needs Manual Review — no matching facility ID found
          </div>
          <ul class="list-group list-group-flush">
            <li class="list-group-item" v-for="(item, i) in unmatched" :key="i">
              {{ item.agency_name || '(no name)' }} — {{ item.local_government || '—' }}
            </li>
          </ul>
        </div>

        <!-- ── Matched facilities ──────────────────────────────── -->
        <div class="card shadow-sm table-card" v-if="matched.length">
          <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
            <label class="mb-0" style="cursor: pointer;">
              <input type="checkbox" :checked="allSelected" @change="toggleSelectAll($event.target.checked)" class="mr-2" />
              Select All
            </label>
            <button class="btn btn-danger btn-sm" :disabled="selectedCount === 0 || processing" @click="disapproveSelected">
              <i class="fe fe-slash mr-1"></i>
              {{ processing ? `Processing ${processedCount}/${selectedTotal}…` : `Disapprove Selected (${selectedCount})` }}
            </button>
          </div>
          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th px-4"></th>
                  <th class="table-th">Facility Name</th>
                  <th class="table-th">LGA</th>
                  <th class="table-th">ID</th>
                  <th class="table-th">Result</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in matched" :key="item.id" class="table-row">
                  <td class="px-4"><input type="checkbox" :aria-label="'Select ' + item.facility_name" v-model="item.selected" :disabled="processing" /></td>
                  <td class="font-weight-bold" style="font-size: .88rem;">{{ item.facility_name }}</td>
                  <td style="font-size: .88rem;">{{ item.local_government || '—' }}</td>
                  <td style="font-size: .82rem;">{{ item.id }}</td>
                  <td>
                    <span v-if="item.result === 'success'" class="badge badge-success">Disapproved</span>
                    <span v-else-if="item.result === 'already_disapproved'" class="badge badge-secondary">Already Disapproved</span>
                    <span v-else-if="item.result === 'error'" class="badge badge-danger">Failed</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="card shadow-sm" v-if="loaded && !matched.length && !unmatched.length">
          <div class="card-body text-center text-muted py-5">
            <i class="fe fe-check-circle" style="font-size: 2rem; display: block; margin-bottom: 8px;"></i>
            No zero-enrollee facilities found.
          </div>
        </div>

      </div>

      <div class="vld-parent">
        <loading :active.sync="loading" loader="dots" :can-cancel="false" :is-full-page="true"></loading>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: {
    Navbar,
    Loading,
  },
  data() {
    return {
      loading: false,
      loaded: false,
      processing: false,
      processedCount: 0,
      selectedTotal: 0,
      matched: [],
      unmatched: [],
    };
  },
  computed: {
    pendingItems() {
      return this.matched.filter((i) => i.result !== "already_disapproved");
    },
    alreadyDisapprovedCount() {
      return this.matched.length - this.pendingItems.length;
    },
    allSelected() {
      return this.pendingItems.length > 0 && this.pendingItems.every((i) => i.selected);
    },
    selectedCount() {
      return this.matched.filter((i) => i.selected).length;
    },
  },
  methods: {
    normalize(s) {
      return String(s || "").trim().toLowerCase();
    },
    toggleSelectAll(checked) {
      this.pendingItems.forEach((i) => {
        i.selected = checked;
      });
    },
    async loadZeroEnrolleeFacilities() {
      this.loading = true;
      this.loaded = false;
      this.matched = [];
      this.unmatched = [];
      try {
        const [statsRes, providersRes] = await Promise.all([
          this.axios.get(`/api/v1/auth/provider/stats`, { params: { agency_id: agency.id } }),
          this.axios.get(`/api/v1/auth/providerAgency/${agency.id}`),
        ]);
        const zeroEnrollee = (statsRes.data.data || []).filter((item) => item.total <= 0);
        const providerByName = new Map(
          (providersRes.data.data || []).map((p) => [this.normalize(p.agency_name), p])
        );

        zeroEnrollee.forEach((item) => {
          const match = providerByName.get(this.normalize(item.agency_name));
          if (match) {
            const alreadyDisapproved = match.status === false || match.status === 0;
            this.matched.push({
              id: match.id,
              facility_name: item.agency_name,
              local_government: item.local_government,
              selected: !alreadyDisapproved,
              result: alreadyDisapproved ? "already_disapproved" : null,
            });
          } else {
            this.unmatched.push(item);
          }
        });
        this.loaded = true;
      } catch (error) {
        console.error(error);
        this.$toasted.error("Failed to load facilities", { position: "top-center", duration: 3000 });
      } finally {
        this.loading = false;
      }
    },
    async disapproveSelected() {
      const selected = this.matched.filter((i) => i.selected);
      if (!selected.length) return;
      if (
        !confirm(
          `Disapprove ${selected.length} facilities with zero enrollees?\n\nThis hides them from active use but does not delete any data. Each one can be re-approved individually later from its own facility page.`
        )
      ) {
        return;
      }

      this.processing = true;
      this.processedCount = 0;
      this.selectedTotal = selected.length;
      let successCount = 0;
      let failCount = 0;

      for (const item of selected) {
        try {
          await this.axios.post("/api/v1/auth/approveDisapproveProviderByAgency", {
            agency_id: agency.id,
            provider_id: item.id,
            status: 0,
          });
          item.result = "success";
          successCount++;
        } catch (error) {
          console.error(`Failed to disapprove ${item.facility_name} (${item.id})`, error);
          item.result = "error";
          failCount++;
        }
        this.processedCount++;
      }

      this.processing = false;
      this.$toasted.info(`Done: ${successCount} disapproved, ${failCount} failed`, {
        position: "top-center",
        duration: 6000,
      });
    },
  },
  created() {
    this.loadZeroEnrolleeFacilities();
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.header-icon-wrap {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.table-card {
  border-radius: 14px;
  border: none;
  overflow: hidden;
}

.table-head {
  background: #f8fafc;
}

.table-th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
  border-bottom: 2px solid #e2e8f0;
  padding: 12px 16px;
  white-space: nowrap;
}

.table-row {
  border-color: #f1f5f9;
}

.table-row:hover {
  background: #f8fafc;
}
</style>
